// src/features/organisation/components/hierarchy/HierarchyDetailForm.tsx
import React, { useMemo } from 'react';
import { GitBranch } from 'lucide-react';
import { apiClient } from '../../../../services/api/client';
import { useEmployees } from '../../hooks/useEmployees';
import { useSites } from '../../hooks/useSites';
import { useDepartments } from '../../hooks/useDepartments';
import { Employee } from '../../../../types/organisation/employees.types';
import { Site } from '../../../../types/organisation/sites.types';
import { Department } from '../../../../types/organisation/departments.types';
import {
  EntityFormLayout,
  EntityFormHeader,
  EntityFormFooter,
  EditableField
} from '../../../../components/common/forms';
import { useEntityForm, FieldConfig } from '../../../../components/common/hooks';

interface EmployeeHierarchy {
  id?: number;
  tenantId?: string;
  employeeId: number;
  managerId: number;
  siteId: number;
  departmentId?: number;
  hierarchyTypeCode: string;
  responsibilityLevel: number;
  delegationReason?: string;
  appliesToAllSites?: boolean;
  isActive?: boolean;
  effectiveFrom: string;
  effectiveTo?: string;
}

const HIERARCHY_TYPES = [
  { value: 'FUNCTIONAL', label: 'Fonctionnelle' },
  { value: 'OPERATIONAL', label: 'Opérationnelle' },
  { value: 'ADMINISTRATIVE', label: 'Administrative' },
];

interface HierarchyDetailFormProps {
  hierarchyId: number | null;
  onClose: () => void;
  onSave?: () => void;
}

const HierarchyDetailForm: React.FC<HierarchyDetailFormProps> = ({
  hierarchyId,
  onClose,
  onSave: onSaveCallback,
}) => {
  const { employees } = useEmployees();
  const { sites } = useSites();
  const { departments } = useDepartments();
  const [loading, setLoading] = React.useState(!!hierarchyId);
  const [hierarchy, setHierarchy] = React.useState<EmployeeHierarchy | null>(null);

  React.useEffect(() => {
    if (hierarchyId) {
      setLoading(true);
      apiClient.get(`/organisation/employee-hierarchies/${hierarchyId}`)
        .then((data: EmployeeHierarchy) => setHierarchy(data))
        .finally(() => setLoading(false));
    } else {
      setHierarchy(null);
    }
  }, [hierarchyId]);

  const fields: FieldConfig[] = useMemo(() => [
    {
      name: 'employeeId',
      label: 'Employé',
      type: 'select',
      required: true,
      options: (employees || []).map((e: Employee) => ({
        value: e.id,
        label: `${e.firstName} ${e.lastName}`,
      })),
      render: (value) => {
        const emp = (employees || []).find((e: Employee) => e.id === value);
        return emp ? `${emp.firstName} ${emp.lastName}` : 'Non défini';
      },
    },
    {
      name: 'managerId',
      label: 'Manager',
      type: 'select',
      required: true,
      options: (employees || []).map((e: Employee) => ({
        value: e.id,
        label: `${e.firstName} ${e.lastName}`,
      })),
      render: (value) => {
        const emp = (employees || []).find((e: Employee) => e.id === value);
        return emp ? `${emp.firstName} ${emp.lastName}` : 'Non défini';
      },
    },
    {
      name: 'siteId',
      label: 'Site',
      type: 'select',
      required: true,
      options: (sites || []).map((s: Site) => ({ value: s.id, label: s.siteName })),
      render: (value) => (sites || []).find((s: Site) => s.id === value)?.siteName || 'Non défini',
    },
    {
      name: 'departmentId',
      label: 'Département',
      type: 'select',
      options: [
        { value: 0, label: '— Aucun —' },
        ...(departments || []).map((d: Department) => ({ value: d.id, label: d.departmentName })),
      ],
      render: (value) => {
        if (!value) return '—';
        return (departments || []).find((d: Department) => d.id === value)?.departmentName || '—';
      },
    },
    {
      name: 'hierarchyTypeCode',
      label: 'Type de hiérarchie',
      type: 'select',
      required: true,
      options: HIERARCHY_TYPES,
      render: (value) => HIERARCHY_TYPES.find(t => t.value === value)?.label || value,
    },
    {
      name: 'responsibilityLevel',
      label: 'Niveau de responsabilité',
      type: 'number',
    },
    {
      name: 'effectiveFrom',
      label: "Date d'effet",
      type: 'date',
      required: true,
    },
    {
      name: 'effectiveTo',
      label: 'Date de fin',
      type: 'date',
    },
    {
      name: 'delegationReason',
      label: 'Raison de la délégation',
      type: 'text',
      maxLength: 500,
      colSpan: 2,
    },
  ], [employees, sites, departments]);

  const transformToFormData = (h: (EmployeeHierarchy & { employee?: { id: number; firstName: string; lastName: string }; manager?: { id: number }; site?: { id: number }; department?: { id: number } }) | null) => {
    if (!h) return {
      employeeId: null, managerId: null, siteId: null, departmentId: null,
      hierarchyTypeCode: 'FUNCTIONAL', responsibilityLevel: 1,
      effectiveFrom: new Date().toISOString().split('T')[0],
      effectiveTo: '', delegationReason: '',
    };
    return {
      // Le backend retourne des objets imbriqués (employee, manager, site) ou des IDs plats selon le contexte
      employeeId: h.employeeId || h.employee?.id || null,
      managerId: h.managerId || h.manager?.id || null,
      siteId: h.siteId || h.site?.id || null,
      departmentId: h.departmentId || h.department?.id || null,
      hierarchyTypeCode: h.hierarchyTypeCode,
      responsibilityLevel: h.responsibilityLevel,
      effectiveFrom: h.effectiveFrom ? h.effectiveFrom.split('T')[0] : '',
      effectiveTo: h.effectiveTo ? h.effectiveTo.split('T')[0] : '',
      delegationReason: h.delegationReason || '',
    };
  };

  const handleSave = async (formData: Record<string, unknown>) => {
    const data = {
      employeeId: formData.employeeId,
      managerId: formData.managerId,
      siteId: formData.siteId,
      departmentId: formData.departmentId || undefined,
      hierarchyTypeCode: formData.hierarchyTypeCode,
      responsibilityLevel: formData.responsibilityLevel,
      effectiveFrom: formData.effectiveFrom,
      effectiveTo: formData.effectiveTo || undefined,
      delegationReason: formData.delegationReason || undefined,
    };
    if (hierarchyId) {
      await apiClient.put(`/organisation/employee-hierarchies/${hierarchyId}?updatedById=1`, data);
    } else {
      await apiClient.post('/organisation/employee-hierarchies?createdById=1', data);
    }
    onSaveCallback?.();
    onClose();
    return data;
  };

  const {
    formData,
    hasChanges,
    saving,
    errors,
    handleFieldChange,
    startEditing,
    stopEditing,
    isEditing,
    handleSave: onFormSave,
  } = useEntityForm({
    entity: hierarchy,
    fields,
    onSave: handleSave,
    transformToFormData,
  });

  const mainFields = fields.filter(f =>
    ['employeeId', 'managerId', 'siteId', 'departmentId', 'hierarchyTypeCode', 'responsibilityLevel'].includes(f.name)
  );
  const periodFields = fields.filter(f =>
    ['effectiveFrom', 'effectiveTo', 'delegationReason'].includes(f.name)
  );

  const employeeLabel = hierarchy
    ? (() => {
        const h = hierarchy as EmployeeHierarchy & { employee?: { id: number; firstName: string; lastName: string } };
        if (h.employee?.firstName) return `${h.employee.firstName} ${h.employee.lastName}`;
        const emp = (employees || []).find((e: Employee) => e.id === h.employeeId);
        return emp ? `${emp.firstName} ${emp.lastName}` : undefined;
      })()
    : undefined;

  return (
    <EntityFormLayout loading={loading} maxWidth="4xl">
      <EntityFormHeader
        title={hierarchy ? `Hiérarchie — ${employeeLabel || ''}` : 'Nouvelle relation hiérarchique'}
        icon={GitBranch}
        hasChanges={hasChanges}
        saving={saving}
        onSave={onFormSave}
        onClose={onClose}
      />

      <div className="flex-1 overflow-y-auto p-6">
        {formData && (
          <>
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Relation</h3>
              <div className="grid grid-cols-2 gap-4">
                {mainFields.map(field => (
                  <div key={field.name} className={field.colSpan === 2 ? 'col-span-2' : ''}>
                    <EditableField
                      field={field}
                      value={formData[field.name]}
                      isEditing={isEditing(field.name)}
                      onStartEdit={() => startEditing(field.name)}
                      onStopEdit={() => stopEditing(field.name)}
                      onChange={(value) => handleFieldChange(field.name, value)}
                      error={errors[field.name]}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Période & Notes</h3>
              <div className="grid grid-cols-2 gap-4">
                {periodFields.map(field => (
                  <div key={field.name} className={field.colSpan === 2 ? 'col-span-2' : ''}>
                    <EditableField
                      field={field}
                      value={formData[field.name]}
                      isEditing={isEditing(field.name)}
                      onStartEdit={() => startEditing(field.name)}
                      onStopEdit={() => stopEditing(field.name)}
                      onChange={(value) => handleFieldChange(field.name, value)}
                      error={errors[field.name]}
                    />
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      <EntityFormFooter helpText="💡 Double-cliquez sur un champ pour le modifier." />
    </EntityFormLayout>
  );
};

export default HierarchyDetailForm;
